import React, { useState } from 'react'
import {
  motion,
  useReducedMotion,
  useMotionValue,
  useSpring,
  useTransform,
  useMotionTemplate,
} from 'framer-motion'

const EASE = [0.22, 1, 0.36, 1]

const item = {
  hidden: { opacity: 0, y: 18 },
  show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: EASE } },
}

const gridStyle = {
  backgroundImage:
    'linear-gradient(to right, rgba(255,255,255,0.35) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,0.35) 1px, transparent 1px)',
  backgroundSize: '28px 28px',
}

// fixed-position twinkling stars ('full' mode only)
const stars = [
  { top: '14%', left: '8%', size: 3, delay: 0, duration: 3.4 },
  { top: '70%', left: '18%', size: 2, delay: 0.8, duration: 4 },
  { top: '22%', left: '85%', size: 3, delay: 1.6, duration: 3.8 },
  { top: '55%', left: '92%', size: 2, delay: 2.4, duration: 4.4 },
  { top: '85%', left: '55%', size: 2, delay: 1.1, duration: 3.6 },
]

// Use inside AnimatedCard to get the staggered blur-in reveal
export function MotionItem({ children, className = '', lift = false }) {
  return (
    <motion.div
      variants={item}
      className={className}
      whileHover={lift ? { y: -5, scale: 1.02 } : undefined}
      transition={lift ? { type: 'spring', stiffness: 300, damping: 20 } : undefined}
    >
      {children}
    </motion.div>
  )
}

/**
 * tilt:      3D tilt + cursor spotlight + grid light + border brighten (turn off for big/long cards)
 * effects:   'full' = + twinkling stars & a rare shooting star, 'light' = border beam + glow only
 * delay:     entrance delay in seconds (to stagger cards on the page)
 * enterFrom: 'bottom' (default) | 'left' | 'right' - direction the card slides in from on load
 * animate:   false skips the mount entrance entirely (use for skeletons, so the slide-in
 *            only plays once the real content mounts, not for the placeholder too)
 */
export default function AnimatedCard({
  children,
  className = '',
  tilt = true,
  effects = 'full',
  delay = 0,
  enterFrom = 'bottom',
  animate = true,
}) {
  const reduceMotion = useReducedMotion()
  const [hovered, setHovered] = useState(false)
  const full = effects === 'full'
  const interactive = tilt && !reduceMotion

  const hiddenOffset =
    enterFrom === 'left'
      ? { x: -72, y: 0 }
      : enterFrom === 'right'
      ? { x: 72, y: 0 }
      : { x: 0, y: 36 }

  const container = {
    hidden: { opacity: 0, ...hiddenOffset, scale: 0.98 },
    show: {
      opacity: 1,
      x: 0,
      y: 0,
      scale: 1,
      transition: { duration: 0.7, ease: EASE, when: 'beforeChildren', staggerChildren: 0.1 },
    },
  }

  const px = useMotionValue(0.5)
  const py = useMotionValue(0.5)
  const mx = useMotionValue(0)
  const my = useMotionValue(0)

  const sx = useSpring(px, { stiffness: 120, damping: 20 })
  const sy = useSpring(py, { stiffness: 120, damping: 20 })
  const rotateY = useTransform(sx, [0, 1], [-3.5, 3.5])
  const rotateX = useTransform(sy, [0, 1], [3.5, -3.5])

  const spotlight = useMotionTemplate`radial-gradient(360px circle at ${mx}px ${my}px, rgba(255,255,255,0.1), transparent 70%)`
  const gridMask = useMotionTemplate`radial-gradient(180px circle at ${mx}px ${my}px, #000, transparent)`
  const borderMask = useMotionTemplate`radial-gradient(180px circle at ${mx}px ${my}px, rgba(255,255,255,0.95), transparent)`

  const handleMove = (e) => {
    if (!interactive) return
    const rect = e.currentTarget.getBoundingClientRect()
    const x = e.clientX - rect.left
    const y = e.clientY - rect.top
    mx.set(x)
    my.set(y)
    px.set(x / rect.width)
    py.set(y / rect.height)
  }

  const handleLeave = () => {
    setHovered(false)
    px.set(0.5)
    py.set(0.5)
  }

  return (
    <motion.div
      variants={{
        ...container,
        show: { ...container.show, transition: { ...container.show.transition, delay } },
      }}
      initial={reduceMotion || !animate ? 'show' : 'hidden'}
      animate="show"
      onMouseMove={handleMove}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={handleLeave}
      whileHover={interactive ? { scale: 1.01 } : undefined}
      style={interactive ? { rotateX, rotateY, transformPerspective: 1100 } : undefined}
      className={`relative isolate overflow-hidden transition-shadow duration-500 hover:shadow-[0_24px_70px_-20px_rgba(255,255,255,0.22)] ${className}`}
    >
      {/* all effects live behind the content */}
      <div aria-hidden className="pointer-events-none absolute inset-0 -z-10">
        {/* rotating light along the border (1px ring) */}
        {!reduceMotion && (
          <div
            className="absolute -inset-px overflow-hidden rounded-[15px] p-px"
            style={{
              WebkitMask: 'linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)',
              WebkitMaskComposite: 'xor',
              mask: 'linear-gradient(#000 0 0) content-box exclude, linear-gradient(#000 0 0)',
            }}
          >
            <div className="flex h-full w-full items-center justify-center">
              <motion.div
                className="aspect-square w-[250%] shrink-0"
                style={{
                  background:
                    'conic-gradient(from 0deg, transparent 0%, transparent 60%, rgba(255,255,255,0.15) 80%, rgba(255,255,255,0.95) 92%, transparent 100%)',
                }}
                animate={{ rotate: 360 }}
                transition={{ duration: 7, repeat: Infinity, ease: 'linear' }}
              />
            </div>
          </div>
        )}

        {/* border brightens around the cursor (1px ring) */}
        {interactive && (
          <motion.div
            className="absolute -inset-px overflow-hidden rounded-[15px] p-px"
            style={{
              WebkitMask: 'linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)',
              WebkitMaskComposite: 'xor',
              mask: 'linear-gradient(#000 0 0) content-box exclude, linear-gradient(#000 0 0)',
              background: borderMask,
            }}
            animate={{ opacity: hovered ? 1 : 0 }}
            transition={{ duration: 0.4 }}
          />
        )}

        {/* faint grid */}
        <div
          className="absolute inset-0 opacity-[0.06]"
          style={{
            ...gridStyle,
            WebkitMaskImage: 'radial-gradient(ellipse at top right, #000, transparent 70%)',
            maskImage: 'radial-gradient(ellipse at top right, #000, transparent 70%)',
          }}
        />

        {/* grid lights up under cursor */}
        {interactive && (
          <motion.div
            className="absolute inset-0"
            style={{ ...gridStyle, WebkitMaskImage: gridMask, maskImage: gridMask }}
            animate={{ opacity: hovered ? 0.3 : 0 }}
            transition={{ duration: 0.4 }}
          />
        )}

        {/* cursor spotlight */}
        {interactive && (
          <motion.div
            className="absolute inset-0"
            style={{ background: spotlight }}
            animate={{ opacity: hovered ? 1 : 0 }}
            transition={{ duration: 0.4 }}
          />
        )}

        {/* top-edge highlight */}
        {!reduceMotion && (
          <motion.div
            className="absolute inset-x-8 top-0 h-px bg-gradient-to-r from-transparent via-white/60 to-transparent"
            animate={{ opacity: [0.3, 0.9, 0.3] }}
            transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
          />
        )}

        {/* drifting aurora blobs */}
        {!reduceMotion && (
          <>
            <motion.div
              className="absolute -top-24 -right-24 h-60 w-60 rounded-full bg-white/10 blur-3xl"
              animate={{ x: [0, -30, 0], y: [0, 25, 0], scale: [1, 1.3, 1], opacity: [0.4, 0.9, 0.4] }}
              transition={{ duration: 10, repeat: Infinity, ease: 'easeInOut' }}
            />
            {full && (
              <motion.div
                className="absolute -bottom-24 -left-24 h-52 w-52 rounded-full bg-[#A0AEC0]/15 blur-3xl"
                animate={{ x: [0, 30, 0], y: [0, -20, 0], scale: [1.2, 1, 1.2], opacity: [0.3, 0.7, 0.3] }}
                transition={{ duration: 12, repeat: Infinity, ease: 'easeInOut' }}
              />
            )}
          </>
        )}

        {/* sheen sweep across the whole card */}
        {!reduceMotion && (
          <motion.div
            className="absolute -inset-y-4 -left-1/3 w-1/3 -skew-x-12 bg-gradient-to-r from-transparent via-white/[0.09] to-transparent"
            initial={{ x: '0%' }}
            animate={{ x: '260%' }}
            transition={{ duration: 1.2, delay: delay + 0.7, repeat: Infinity, repeatDelay: 8, ease: 'easeInOut' }}
          />
        )}

        {/* twinkling stars */}
        {full &&
          !reduceMotion &&
          stars.map((s, i) => (
            <motion.span
              key={i}
              className="absolute rounded-full bg-white"
              style={{
                top: s.top,
                left: s.left,
                width: s.size,
                height: s.size,
                boxShadow: '0 0 6px rgba(255,255,255,0.9)',
              }}
              animate={{ opacity: [0, 0.9, 0], scale: [0.6, 1.2, 0.6] }}
              transition={{ duration: s.duration, delay: s.delay, repeat: Infinity, ease: 'easeInOut' }}
            />
          ))}

        {/* one rare shooting star */}
        {full && !reduceMotion && (
          <motion.span
            className="absolute h-px w-24 bg-gradient-to-r from-transparent to-white"
            style={{ top: '-5%', left: '20%', rotate: 45 }}
            initial={{ x: 0, y: 0, opacity: 0 }}
            animate={{ x: 260, y: 260, opacity: [0, 1, 0] }}
            transition={{ duration: 1.6, delay: 2, repeat: Infinity, repeatDelay: 9, ease: 'easeIn' }}
          />
        )}
      </div>

      {children}
    </motion.div>
  )
}
